import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { LoadingSection } from '#/components/LoadingSection/LoadingSection';
import { salariesFetchSynonyms } from '#/modules/salaries/api/salariesFetchSynonyms';
import type { SalariesJobTitle } from '#/modules/salaries/types/SalariesJobTitle';

import { JobDescription } from './JobDescription';
import { NoJobsMatch } from './NoJobsMatch';
import { RelatedJobs } from './RelatedJobs';

type JobSibling = {
  description: string;
  family: { id: string; name: string };
  id: string;
  level: number;
  name: string;
  subFamily: { id: string };
  synonyms: {
    id: string;
    name: string;
  }[];
};

type Props = {
  id: string | null | undefined;
  title: string;
  onSelect: (arg0: {
    familyId: string;
    id: string;
    name: string;
    synonymId: string | null;
  }) => void;
  onReset: () => void;
  onContinue?: () => void;
  suggestion?: {
    name: string;
    jobTitle: SalariesJobTitle;
    objectID: string;
  };
};

type JobSiblingWithTree = JobSibling & {
  tree: JobSibling[];
};

export function JobSelector({
  id,
  title,
  onSelect,
  onReset,
  onContinue,
  suggestion,
}: Props): ReactElement | null {
  const [isLoading, setLoading] = useState(true);
  const [job, setJob] = useState<JobSiblingWithTree | null>(null);
  const [relatedJobsShowing, setRelatedJobsShowing] = useState(false);

  useEffect(() => {
    setRelatedJobsShowing(false);

    if (!id) {
      setJob(null);
      setLoading(false);
    } else {
      salariesFetchSynonyms(id).then(
        ({
          jobTitle,
          subFamilyJobTitles,
        }: {
          jobTitle: JobSibling;
          subFamilyJobTitles: JobSibling[];
        }) => {
          setJob({ ...jobTitle, tree: subFamilyJobTitles });
          setLoading(false);
        },
      );
    }
  }, [id, setJob]);

  useEffect(() => {
    setRelatedJobsShowing(false);
  }, [title]);

  const siblings = useMemo(() => {
    if (!job) return null;

    const allSiblings = job.tree
      // oxlint-disable-next-line typescript/no-explicit-any
      .filter((sibling: any) => sibling.id !== job.id)
      // oxlint-disable-next-line typescript/no-explicit-any
      .sort((a: any, b: any) => b.level - a.level);

    const lowerLevel = allSiblings
      // oxlint-disable-next-line typescript/no-explicit-any
      .filter((sibling: any) => sibling.level > job.level)
      .reverse();

    const higherLevel = allSiblings.filter(
      // oxlint-disable-next-line typescript/no-explicit-any
      (sibling: any) => sibling.level < job.level,
    );

    const closestSiblings =
      // TODO: Fix this the next time the file is edited.
      // oxlint-disable-next-line no-nested-ternary
      lowerLevel.length && higherLevel.length
        ? [lowerLevel[0], higherLevel[0]]
        : lowerLevel.length
          ? lowerLevel.slice(0, 2).reverse()
          : higherLevel.slice(0, 2);

    return closestSiblings.length ? closestSiblings : null;
  }, [job]);

  if (isLoading) {
    return <LoadingSection noDelay />;
  }

  if (!job) {
    return (
      <NoJobsMatch
        suggestion={suggestion}
        onSelect={onSelect}
        title={title}
        onContinue={onContinue}
      />
    );
  }

  if (!relatedJobsShowing) {
    return (
      <JobDescription
        job={job}
        title={title}
        siblings={siblings}
        setRelatedJobsShowing={setRelatedJobsShowing}
        onReset={onReset}
      />
    );
  }

  if (siblings) {
    return (
      <RelatedJobs
        onSelect={onSelect}
        onReset={onReset}
        siblings={siblings}
        setRelatedJobsShowing={setRelatedJobsShowing}
      />
    );
  }

  return null;
}
